import type { SingPitchId } from './clingCantaConfig';
import { SING_PITCH_BY_ID, SING_MIN_MIDI, SING_MAX_MIDI } from './clingCantaConfig';
import { clingRollContentHeight } from './clingCantaPitch';

/** Cling anclado a ~22% del ancho (mira hacia la derecha). */
export const CLING_SCREEN_X_RATIO = 0.22;

/** Suelo rocoso desde ~78% de alto. */
export const CLING_GROUND_RATIO = 0.78;

export const CLING_FLY_TOP_RATIO = 0.1;

export const CLING_GRAVITY_PX_MS = 0.00022;

export const CLING_COLLECT_RADIUS_X = 42;
export const CLING_COLLECT_RADIUS_Y = 36;

export function clingScreenX(stageWidth: number): number {
  return stageWidth * CLING_SCREEN_X_RATIO;
}

export function groundY(stageHeight: number): number {
  return stageHeight * CLING_GROUND_RATIO;
}

export function flyZoneBounds(stageHeight: number): { top: number; bottom: number } {
  return {
    top: stageHeight * CLING_FLY_TOP_RATIO,
    bottom: groundY(stageHeight) - 28,
  };
}

/** Zona de vuelo en exploración: ocupa casi todo el alto del escenario. */
export function exploreFlyZoneBounds(stageHeight: number): { top: number; bottom: number } {
  const pad = Math.max(14, Math.round(stageHeight * 0.035));
  return { top: pad, bottom: stageHeight - pad };
}

export function exploreLaneBandHeight(stageHeight: number, laneCount: number): number {
  const { top, bottom } = exploreFlyZoneBounds(stageHeight);
  return (bottom - top) / Math.max(1, laneCount);
}

export function exploreLaneBandTop(
  pitchId: SingPitchId,
  lanes: SingPitchId[],
  stageHeight: number
): number {
  const idx = lanes.indexOf(pitchId);
  const { top } = exploreFlyZoneBounds(stageHeight);
  if (idx < 0) return top;
  return top + idx * exploreLaneBandHeight(stageHeight, lanes.length);
}

/** Y de una nota / Cling en exploración, repartida en todo el alto jugable. */
export function explorePitchY(
  pitchId: SingPitchId,
  lanes: SingPitchId[],
  stageHeight: number
): number {
  const { top, bottom } = exploreFlyZoneBounds(stageHeight);
  if (lanes.length <= 1) return (top + bottom) / 2;

  const minMidi = SING_PITCH_BY_ID[lanes[lanes.length - 1]!]?.midi ?? SING_MIN_MIDI;
  const maxMidi = SING_PITCH_BY_ID[lanes[0]!]?.midi ?? SING_MAX_MIDI;
  const def = SING_PITCH_BY_ID[pitchId];
  const clamped = Math.min(maxMidi, Math.max(minMidi, def?.midi ?? minMidi));
  const t = (clamped - minMidi) / Math.max(1, maxMidi - minMidi);
  return top + (1 - t) * (bottom - top);
}

export function exploreMidiToFlyY(
  midi: number,
  lanes: SingPitchId[],
  stageHeight: number
): number {
  const { top, bottom } = exploreFlyZoneBounds(stageHeight);
  if (lanes.length <= 1) return (top + bottom) / 2;

  const minMidi = SING_PITCH_BY_ID[lanes[lanes.length - 1]!]?.midi ?? SING_MIN_MIDI;
  const maxMidi = SING_PITCH_BY_ID[lanes[0]!]?.midi ?? SING_MAX_MIDI;
  const clamped = Math.min(maxMidi, Math.max(minMidi, midi));
  const t = (clamped - minMidi) / Math.max(1, maxMidi - minMidi);
  return top + (1 - t) * (bottom - top);
}

export function explorePitchOrMidiToFlyY(
  midi: number | null,
  pitchId: SingPitchId | null,
  lanes: SingPitchId[],
  stageHeight: number
): number | null {
  if (midi != null) return exploreMidiToFlyY(midi, lanes, stageHeight);
  if (pitchId) return explorePitchY(pitchId, lanes, stageHeight);
  return null;
}

export function pitchToFlyY(
  pitchId: SingPitchId,
  lanes: SingPitchId[],
  stageHeight: number
): number {
  const def = SING_PITCH_BY_ID[pitchId];
  if (!def) return (flyZoneBounds(stageHeight).top + flyZoneBounds(stageHeight).bottom) / 2;
  return midiToFlyY(def.midi, lanes, stageHeight);
}

/** Altura continua según MIDI detectado (sigue la voz sin saltar solo a carriles). */
export function midiToFlyY(midi: number, lanes: SingPitchId[], stageHeight: number): number {
  const { top, bottom } = flyZoneBounds(stageHeight);
  if (lanes.length <= 1) return (top + bottom) / 2;

  const minMidi = SING_PITCH_BY_ID[lanes[lanes.length - 1]!]?.midi ?? SING_MIN_MIDI;
  const maxMidi = SING_PITCH_BY_ID[lanes[0]!]?.midi ?? SING_MAX_MIDI;
  const clamped = Math.min(maxMidi, Math.max(minMidi, midi));
  const t = (clamped - minMidi) / Math.max(1, maxMidi - minMidi);
  return top + (1 - t) * (bottom - top);
}

export function pitchOrMidiToFlyY(
  midi: number | null,
  pitchId: SingPitchId | null,
  lanes: SingPitchId[],
  stageHeight: number
): number | null {
  if (midi != null) return midiToFlyY(midi, lanes, stageHeight);
  if (pitchId) return pitchToFlyY(pitchId, lanes, stageHeight);
  return null;
}

/** Convierte MIDI a coordenada Y interna del motor de canto (espacio de carriles). */
export function midiToContentLaneY(
  midi: number,
  lanes: SingPitchId[],
  stageHeight: number
): number {
  const { top, bottom } = flyZoneBounds(stageHeight);
  const flyY = midiToFlyY(midi, lanes, stageHeight);
  const contentH = clingRollContentHeight(lanes.length);
  const t = (flyY - top) / Math.max(1, bottom - top);
  return t * contentH;
}

export function pitchToContentLaneY(
  pitchId: SingPitchId,
  lanes: SingPitchId[],
  stageHeight: number
): number {
  const def = SING_PITCH_BY_ID[pitchId];
  return midiToContentLaneY(def?.midi ?? 60, lanes, stageHeight);
}

export function worldToScreenX(worldX: number, scrollX: number): number {
  return worldX - scrollX;
}
